/**
 * @fileoverview Mock backend for the Sales CRM Active Clients read endpoints.
 * @description Registers GET /sales/clients, /sales/clients/summary, /sales/clients/filter-options
 * and /sales/clients/{tenantId} on the feature's shared mock adapter, answering in the backend's
 * envelopes with its error codes (Confluence 3.5.11.27-.30, decision record
 * docs/decisions/2026-11-01-active-clients.md in the backend repo). Nothing here keeps a second copy of
 * the banks: each answer is projected on the spot from the shared Tenant store (see
 * active-clients-mock-projection.ts). Follows the backend's rules: every client is visible to every
 * Sales role; the grid lists active clients unless `status` asks for the archived ones (`ARCHIVED` is
 * an alias of `INACTIVE`), searches the bank name, Bank ID and city literally, sorts newest onboarded
 * first by default and refuses an unknown query parameter or a page size over 100 with 400 / 0001;
 * the KPI tiles are global and count active clients only; the details answer 404 / 4125 for a bank
 * that is not a client. The commands are in the two other active-clients mock services. A no-op when
 * VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  callerOrRefusal,
  clientIdOf,
  clientOrRefusal,
  listClients,
} from './active-clients-mock-guard'
import {
  clientEtagOf,
  clientStatusOf,
  hrAdminStateOf,
  toClientDetails,
  toClientListItem,
} from './active-clients-mock-projection'
import { hrAdminOf } from './active-clients-mock-state'
import { referenceFor } from './sales-crm-mock-projection'
import { failReply, okEnvelope, queryOf } from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import { findSalesUserById } from './sales-roster-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { SalesClientStatus } from '@/features/sales/types/sales-client.types'
import type { Tenant } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

/** @description The query parameters the grid accepts; any other is a 400. */
const GRID_PARAMS = [
  'page',
  'limit',
  'search',
  'stateId',
  'executiveUserId',
  'status',
  'sortBy',
  'sortDirection',
]

/** @description The default page size, and the largest the backend accepts. */
const DEFAULT_LIMIT = 25
const MAX_LIMIT = 100

/**
 * @description The status the grid asks for: active unless it asks for the archived ones.
 * @param {string | undefined} status - The `status` query value, any case.
 * @returns {SalesClientStatus | null} The status, or null for a value the backend refuses.
 */
function requestedStatus(status: string | undefined): SalesClientStatus | null {
  const value = (status ?? 'ACTIVE').toUpperCase()
  if (value === 'ACTIVE') return 'ACTIVE'
  return value === 'INACTIVE' || value === 'ARCHIVED' ? 'INACTIVE' : null
}

/**
 * @description Compares two sort keys of the same kind.
 * @param {string | number} left - The first key.
 * @param {string | number} right - The second key.
 * @returns {number} Negative, zero or positive, like a sort comparator.
 */
function compareKeys(left: string | number, right: string | number): number {
  if (left < right) return -1
  return left > right ? 1 : 0
}

/**
 * @description Orders clients the way the grid's `sortBy` and `sortDirection` ask.
 * @param {Tenant[]} clients - The matching clients.
 * @param {Record<string, string | undefined>} query - The request's query.
 * @returns {Tenant[]} The clients in order.
 */
function sorted(clients: Tenant[], query: Record<string, string | undefined>): Tenant[] {
  const sortBy = query.sortBy ?? 'onboardedAt'
  const flip = (query.sortDirection ?? '').toLowerCase()
  const ascending = flip === '' ? sortBy === 'bankName' || sortBy === 'tenantCode' : flip === 'asc'
  const key = (tenant: Tenant): string | number => {
    if (sortBy === 'bankName') return tenant.name.toLowerCase()
    if (sortBy === 'tenantCode') return tenant.id
    if (sortBy === 'branchCount') return tenant.branches
    return tenant.clientSince ?? tenant.createdAt
  }
  return [...clients].sort((a, b) => {
    const order = compareKeys(key(a), key(b))
    return ascending ? order : -order
  })
}

/**
 * @description Finds what the backend would refuse in the grid's query with 400 / 0001.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {Record<string, string | undefined>} query - The parsed query.
 * @returns {string | null} What is wrong, or null when the query is valid.
 */
function invalidGridQuery(
  config: AxiosRequestConfig,
  query: Record<string, string | undefined>,
): string | null {
  const unknown = Object.keys((config.params ?? {}) as object).find(
    (name) => !GRID_PARAMS.includes(name),
  )
  if (unknown) return `Unknown query parameter ${unknown}`
  if (requestedStatus(query.status) === null) return 'status must be ACTIVE or INACTIVE'
  const limit = Number(query.limit ?? DEFAULT_LIMIT)
  if (!Number.isInteger(limit) || limit < 1 || limit > MAX_LIMIT) return 'limit must be 1-100'
  return Number(query.page ?? 1) >= 1 ? null : 'page must be 1 or more'
}

/**
 * @description Answers the grid: one page of the clients matching the filters.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The page and its meta, or the backend's error.
 */
function listReply(config: AxiosRequestConfig): MockReply {
  const caller = callerOrRefusal()
  if (Array.isArray(caller)) return caller
  const query = queryOf(config)
  const invalid = invalidGridQuery(config, query)
  if (invalid) return failReply(400, '0001', invalid)

  const status = requestedStatus(query.status)
  const search = (query.search ?? '').trim().toLowerCase()
  const matching = listClients().filter((tenant) => {
    if (clientStatusOf(tenant) !== status) return false
    if (query.stateId && referenceFor('states', tenant.state).id !== query.stateId) return false
    if (query.executiveUserId && tenant.currentOwnerId !== query.executiveUserId) return false
    return (
      !search ||
      [tenant.name, tenant.id, tenant.city].some((text) => text.toLowerCase().includes(search))
    )
  })
  const page = Number(query.page ?? 1)
  const limit = Number(query.limit ?? DEFAULT_LIMIT)
  const items = sorted(matching, query)
    .slice((page - 1) * limit, page * limit)
    .map(toClientListItem)
  return [
    200,
    okEnvelope(items, {
      page,
      limit,
      total: matching.length,
      totalPages: Math.ceil(matching.length / limit),
    }),
  ]
}

/**
 * @description Answers the KPI tiles: global, counting active clients only.
 * @returns {MockReply} The four tile values, or the backend's error.
 */
function summaryReply(): MockReply {
  const caller = callerOrRefusal()
  if (Array.isArray(caller)) return caller
  const active = listClients().filter((tenant) => clientStatusOf(tenant) === 'ACTIVE')
  const states = active.map((tenant) => hrAdminStateOf(hrAdminOf(tenant)))
  return [
    200,
    okEnvelope({
      totalClients: active.length,
      branches: active.reduce((sum, tenant) => sum + tenant.branches, 0),
      hrAdminInvited: states.filter((state) => state === 'INVITED').length,
      hrAdminActive: states.filter((state) => state === 'ACTIVE').length,
    }),
  ]
}

/**
 * @description Answers the filter choices: the states and owners of every client, either status.
 * @returns {MockReply} The choices, or the backend's error.
 */
function filterOptionsReply(): MockReply {
  const caller = callerOrRefusal()
  if (Array.isArray(caller)) return caller
  const clients = listClients()
  const states = [...new Set(clients.map((tenant) => tenant.state))].sort()
  const ownerIds = [...new Set(clients.flatMap((tenant) => tenant.currentOwnerId ?? []))]
  const executives = ownerIds
    .flatMap((id) => findSalesUserById(id) ?? [])
    .map((user) => ({ userId: user.id, name: user.name, role: user.role }))
    .sort((a, b) => a.name.localeCompare(b.name))
  return [
    200,
    okEnvelope({ states: states.map((state) => referenceFor('states', state)), executives }),
  ]
}

/**
 * @description Answers one client's details, with its version as the ETag.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The details, or the backend's error.
 */
function detailsReply(config: AxiosRequestConfig): MockReply {
  const caller = callerOrRefusal()
  if (Array.isArray(caller)) return caller
  const tenant = clientOrRefusal(clientIdOf(config))
  if (Array.isArray(tenant)) return tenant
  return [200, okEnvelope(toClientDetails(tenant, caller)), { etag: clientEtagOf(tenant) }]
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/clients/summary').reply(summaryReply)
  salesMockAdapter.onGet('/sales/clients/filter-options').reply(filterOptionsReply)
  salesMockAdapter.onGet('/sales/clients').reply(listReply)
  salesMockAdapter.onGet(/^\/sales\/clients\/[^/]+$/).reply(detailsReply)
}
